/**
 * Ship-to editor (S&S auto-send Phase A). Edits FSP's receiving address — the `shippingAddress` sent on
 * every S&S order. Reached from the Purchase-orders area. Mirrors the reorder-config editor: fetch the
 * current value, seed the form (re-seed on refetch so a save reflects latest), POST on save.
 *
 * WRITES are admin / inventory-admin only (server-enforced via requireInventoryWrite); the form is
 * disabled for a read-only viewer (cosmetic — the server 403 is the real gate).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useApiClient } from '@/lib/api/hooks';
import { useAuth } from '@/lib/auth-state';

export function ShipToConfigDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        {open && <ShipToConfigBody onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

function ShipToConfigBody({ onClose }: { onClose: () => void }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const { state } = useAuth();
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;

  const query = useQuery({
    queryKey: ['shipto-config'],
    queryFn: async () => {
      const { data, error } = await client.GET('/shipto-config/current');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the ship-to address.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const [f, setF] = useState({
    customer: '',
    attn: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    emailConfirmation: '',
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF((prev) => ({ ...prev, [k]: e.target.value }));

  // Seed (and re-seed on a refetch) from the server value so a save reflects the latest saved address.
  const cfg = query.data;
  useEffect(() => {
    if (!cfg?.body) return;
    setF({
      customer: cfg.body.customer,
      attn: cfg.body.attn ?? '',
      address: cfg.body.address,
      city: cfg.body.city,
      state: cfg.body.state,
      zip: cfg.body.zip,
      emailConfirmation: cfg.body.emailConfirmation ?? '',
    });
  }, [cfg]);

  // S&S rejects a spelled-out state / non-5-digit ZIP with an opaque 400, so mirror the server's write
  // rules here and block Save before the round-trip. (Kept in sync with SS_STATE_RE / SS_ZIP_RE.)
  const stateOk = /^[A-Za-z]{2}$/.test(f.state.trim());
  const zipOk = /^\d{5}(-\d{4})?$/.test(f.zip.trim());
  const required = f.customer.trim() && f.address.trim() && f.city.trim() && stateOk && zipOk;

  const save = useMutation({
    mutationFn: async () => {
      const { data, error } = await client.POST('/shipto-config', {
        body: {
          customer: f.customer.trim(),
          attn: f.attn.trim() || undefined,
          address: f.address.trim(),
          city: f.city.trim(),
          state: f.state.trim().toUpperCase(),
          zip: f.zip.trim(),
          residential: false,
          emailConfirmation: f.emailConfirmation.trim() || undefined,
        },
      });
      if (error || !data?.ok) throw new Error(error?.error?.detail ?? 'Save failed.');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shipto-config'] });
      onClose();
    },
  });

  const notSet = cfg != null && cfg.body === null;

  const field = (
    key: keyof typeof f,
    label: string,
    opts: { hint?: string; placeholder?: string; maxLength?: number; invalid?: boolean } = {},
  ) => (
    <div className="space-y-1.5">
      <Label htmlFor={`ship-${key}`} className="text-xs">
        {label}
      </Label>
      <Input
        id={`ship-${key}`}
        value={f[key]}
        disabled={!canWrite}
        placeholder={opts.placeholder}
        maxLength={opts.maxLength}
        aria-invalid={opts.invalid || undefined}
        onChange={set(key)}
      />
      {opts.hint && (
        <p className={`text-[11px] ${opts.invalid ? 'text-destructive' : 'text-muted-foreground'}`}>
          {opts.hint}
        </p>
      )}
    </div>
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle>Ship-to address</DialogTitle>
        <DialogDescription>
          Where S&amp;S ships your orders — used as the shipping address on every S&amp;S order you
          send.
        </DialogDescription>
      </DialogHeader>

      {query.isPending && (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      )}
      {query.isError && (
        <p className="text-sm text-destructive">
          {query.error instanceof Error ? query.error.message : 'Failed to load.'}
        </p>
      )}

      {cfg && (
        <div className="space-y-3">
          {notSet && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
              No ship-to set yet. Enter your receiving address to enable sending orders to S&amp;S.
            </p>
          )}
          {!canWrite && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
              Read-only view. Editing the ship-to requires the admin or inventory-admin group.
            </p>
          )}

          {field('customer', 'Company / customer', { placeholder: 'Fast Screen Print' })}
          {field('attn', 'Attn (optional)')}
          {field('address', 'Street address')}
          <div className="grid grid-cols-3 gap-2">
            {field('city', 'City')}
            {field('state', 'State', {
              placeholder: 'TX',
              maxLength: 2,
              invalid: f.state.trim().length > 0 && !stateOk,
              hint: '2-letter code',
            })}
            {field('zip', 'ZIP', {
              placeholder: '78504',
              maxLength: 10,
              invalid: f.zip.trim().length > 0 && !zipOk,
              hint: '5 digits',
            })}
          </div>
          {field('emailConfirmation', 'Confirmation email (optional)', {
            hint: 'S&S emails an order confirmation here. Left blank, no email is sent.',
          })}

          {save.isError && (
            <p className="text-xs text-destructive">
              {save.error instanceof Error ? save.error.message : 'Save failed.'}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => save.mutate()}
              disabled={!canWrite || !required || save.isPending}
            >
              {save.isPending && <Loader2 className="size-4 animate-spin" />}
              Save address
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
